import React, { useState, useEffect } from 'react';
import SpaceBackground from '../components/layout/SpaceBackground';
import {
  Search,
  BookOpen,
  FileText,
  Download,
  Sparkles,
  ChevronRight,
  X,
  Scale,
  FileEdit,
  Loader2,
  ExternalLink,
  Filter,
  Globe,
  AlertCircle,
  Brain,
  MessageSquare,
  Send,
  FolderInput,
  CheckCircle,
  HelpCircle,
} from 'lucide-react';
import api from '../services/api';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const LawLibrary = () => {
  const [activeTab, setActiveTab] = useState('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedStatute, setSelectedStatute] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [stateFilter, setStateFilter] = useState('');
  const [constitutions, setConstitutions] = useState([]);
  const [selectedConstitution, setSelectedConstitution] = useState(null);

  // Document generation state
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateForm, setGenerateForm] = useState({
    document_type: 'motion',
    title: '',
    case_context: '',
    client_name: '',
    opposing_party: '',
    court_name: '',
    case_number: '',
    additional_instructions: '',
    statute_urls: [],
  });
  const [generating, setGenerating] = useState(false);

  // AI Research state
  const [researchQuestion, setResearchQuestion] = useState('');
  const [researchSession, setResearchSession] = useState(null);
  const [researchLoading, setResearchLoading] = useState(false);
  const [followUpAnswer, setFollowUpAnswer] = useState('');
  const [selectedResearchState, setSelectedResearchState] = useState('FL');
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState('');
  const [savingMemo, setSavingMemo] = useState(false);
  const [memoSaved, setMemoSaved] = useState(false);

  const states = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  const documentTypes = [
    { value: 'motion', label: 'Motion' },
    { value: 'brief', label: 'Legal Brief' },
    { value: 'letter', label: 'Legal Letter' },
    { value: 'memo', label: 'Memorandum' },
    { value: 'pleading', label: 'Pleading' },
    { value: 'demand', label: 'Demand Letter' },
    { value: 'discovery', label: 'Discovery Requests' },
  ];

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setPageLoading(true);
      const response = await fetch(`${API_BASE}/api/library/stats`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        setStats({
          total_statutes: 433923,
          states_covered: ['FL', 'CA', 'TX', 'NY', 'GA'],
          features: ['Full-text search', 'AI document generation']
        });
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError('Unable to connect to law library service');
      setStats({
        total_statutes: 433923,
        states_covered: ['FL', 'CA', 'TX', 'NY', 'GA'],
        features: ['Full-text search', 'AI document generation']
      });
    } finally {
      setPageLoading(false);
    }
  };

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    if (!searchQuery.trim()) return;

    setLoading(true);
    try {
      const params = new URLSearchParams({ q: searchQuery });
      if (stateFilter) params.append('state', stateFilter);

      const response = await fetch(`${API_BASE}/api/library/search?${params}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSearchResults(Array.isArray(data) ? data : data.results || []);
      }
    } catch (err) {
      console.error('Search error:', err);
    }
    setLoading(false);
  };

  const fetchStatute = async (url) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/library/statute?url=${encodeURIComponent(url)}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedStatute(data);
      }
    } catch (err) {
      console.error('Error fetching statute:', err);
    }
    setLoading(false);
  };

  const fetchConstitutions = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/library/constitutions`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConstitutions(data.constitutions || []);
      }
    } catch (err) {
      console.error('Error fetching constitutions:', err);
    }
  };

  const downloadPDF = async (url) => {
    try {
      const response = await fetch(`${API_BASE}/api/library/download-pdf?url=${encodeURIComponent(url)}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = 'statute.pdf';
        a.click();
      }
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  const generateDocument = async () => {
    if (!generateForm.title) return;

    setGenerating(true);
    try {
      const response = await fetch(`${API_BASE}/api/library/generate-document`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(generateForm)
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${generateForm.title.replace(/[^a-z0-9]/gi, '_')}.docx`;
        a.click();
        setShowGenerateModal(false);
        setGenerateForm({
          document_type: 'motion',
          title: '',
          case_context: '',
          client_name: '',
          opposing_party: '',
          court_name: '',
          case_number: '',
          additional_instructions: '',
          statute_urls: [],
        });
      }
    } catch (err) {
      console.error('Generation error:', err);
    }
    setGenerating(false);
  };

  const addStatuteToDocument = (url) => {
    if (!generateForm.statute_urls.includes(url)) {
      setGenerateForm({
        ...generateForm,
        statute_urls: [...generateForm.statute_urls, url]
      });
    }
  };

  // Fetch cases for the save-to-case dropdown
  const fetchCases = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/cases/`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCases(data || []);
      }
    } catch (err) {
      console.error('Error fetching cases:', err);
    }
  };

  // Start AI research session
  const startResearch = async () => {
    if (!researchQuestion.trim()) return;

    setResearchLoading(true);
    setResearchSession(null);
    setMemoSaved(false);

    try {
      const response = await api.post('/library/research/start', {
        question: researchQuestion,
        state: selectedResearchState
      });

      const data = response.data;
      // Transform API response to frontend expected format
      const transformedData = {
        ...data,
        status: data.analysis?.needs_clarification
          ? 'needs_clarification'
          : (data.analysis?.analysis_ready ? 'ready_for_memo' : 'analyzing'),
        follow_up_questions: data.analysis?.clarifying_questions || [],
        preliminary_issues: data.analysis?.preliminary_issues || [],
        conversation: [
          { role: 'user', content: researchQuestion },
          {
            role: 'assistant',
            content: data.analysis?.needs_clarification
              ? `I need some clarification to provide accurate research:\n\n${(data.analysis?.clarifying_questions || []).map((q, i) => `${i+1}. ${q}`).join('\n')}`
              : `I've analyzed your question and found ${data.authorities_found || 0} relevant authorities.\n\nKey Legal Issues:\n${(data.analysis?.preliminary_issues || []).map((i, idx) => `• ${i}`).join('\n')}\n\n${data.analysis?.analysis_ready ? 'I\'m ready to generate a comprehensive legal research memo.' : ''}`
          }
        ]
      };
      setResearchSession(transformedData);
      // Fetch cases if we don't have them
      if (cases.length === 0) {
        fetchCases();
      }
    } catch (err) {
      console.error('Research error:', err);
      setError(`Research error: ${err.response?.data?.detail || err.message}`);
    }
    setResearchLoading(false);
  };

  // Submit follow-up answer
  const submitFollowUp = async () => {
    if (!followUpAnswer.trim() || !researchSession) return;

    setResearchLoading(true);
    try {
      const response = await api.post(`/library/research/${researchSession.session_id}/follow-up`, {
        answer: followUpAnswer
      });

      const data = response.data;
      // Transform API response
      const transformedData = {
        ...data,
        status: data.analysis?.needs_clarification
          ? 'needs_clarification'
          : (data.analysis?.analysis_ready ? 'ready_for_memo' : 'analyzing'),
        follow_up_questions: data.analysis?.clarifying_questions || [],
        preliminary_issues: data.analysis?.preliminary_issues || [],
        conversation: [
          ...(researchSession.conversation || []),
          { role: 'user', content: followUpAnswer },
          {
            role: 'assistant',
            content: data.analysis?.needs_clarification
              ? `I need more clarification:\n\n${(data.analysis?.clarifying_questions || []).map((q, i) => `${i+1}. ${q}`).join('\n')}`
              : `Thank you for the clarification. I've found ${data.authorities_found || 0} relevant authorities.\n\nKey Legal Issues:\n${(data.analysis?.preliminary_issues || []).map((i, idx) => `• ${i}`).join('\n')}\n\n${data.analysis?.analysis_ready ? 'I\'m ready to generate a comprehensive legal research memo.' : ''}`
          }
        ]
      };
      setResearchSession(transformedData);
      setFollowUpAnswer('');
    } catch (err) {
      console.error('Follow-up error:', err);
    }
    setResearchLoading(false);
  };

  // Generate research memo
  const generateMemo = async () => {
    if (!researchSession) return;

    setResearchLoading(true);
    try {
      const response = await api.post(`/library/research/${researchSession.session_id}/generate-memo`, {}, {
        responseType: 'blob'
      });

      // The API returns a DOCX file - download it
      const blob = response.data;
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'research_memo.docx';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);

      // Update session status to show memo was generated
      setResearchSession(prev => ({
        ...prev,
        status: 'memo_generated',
        memo: {
          title: `Research Memo: ${prev.question?.substring(0, 50)}...`,
          question_presented: prev.question,
          brief_answer: 'Please see the downloaded document for the full analysis.',
          analysis: 'The comprehensive legal analysis has been generated and downloaded as a Word document.',
          citations: prev.authorities_preview?.map(a => a.title) || [],
          conclusion: 'Review the downloaded memo for complete findings and recommendations.'
        },
        conversation: [
          ...(prev.conversation || []),
          {
            role: 'assistant',
            content: 'I\'ve generated your legal research memorandum. The document has been downloaded automatically. You can also save it to a case using the options on the right.'
          }
        ]
      }));
    } catch (err) {
      console.error('Memo generation error:', err);
    }
    setResearchLoading(false);
  };

  // Save memo to case
  const saveMemoToCase = async () => {
    if (!researchSession || !selectedCase) return;

    setSavingMemo(true);
    try {
      await api.post(`/library/research/${researchSession.session_id}/save-to-case?case_id=${selectedCase}`);
      setMemoSaved(true);
    } catch (err) {
      console.error('Save memo error:', err);
    }
    setSavingMemo(false);
  };

  // Download memo as DOCX
  const downloadMemo = async () => {
    if (!researchSession) return;

    try {
      const response = await api.get(`/library/research/${researchSession.session_id}/download`, {
        responseType: 'blob'
      });

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `research_memo_${researchSession.session_id}.docx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  // Reset research session
  const resetResearch = () => {
    setResearchSession(null);
    setResearchQuestion('');
    setFollowUpAnswer('');
    setMemoSaved(false);
    setSelectedCase('');
  };

  // Loading state
  if (pageLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 p-6 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-virgo-gold-400 animate-spin mx-auto mb-4" />
          <p className="text-virgo-teal-300">Loading Law Library...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 relative">
      <SpaceBackground />
      <div className="relative z-10">
      {/* Error Banner */}
      {error && (
        <div className="mb-4 p-3 bg-red-900/30 border border-red-700 rounded-lg text-red-300 text-sm flex items-center">
          <AlertCircle className="h-4 w-4 mr-2" />
          {error} - Some features may be limited
        </div>
      )}

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-gradient-to-br from-virgo-gold-500 to-virgo-gold-600 rounded-xl shadow-lg">
              <Scale className="h-8 w-8 text-virgo-navy-900" />
            </div>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-virgo-gold-400 to-virgo-gold-300 bg-clip-text text-transparent">
                Law Library
              </h1>
              <p className="text-virgo-teal-300 mt-1">
                {stats ? `${(stats.total_statutes || 0).toLocaleString()} statutes across ${(stats.states_covered || []).length} states` : 'Loading...'}
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowGenerateModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-virgo-gold-500 to-virgo-gold-600 text-virgo-navy-900 rounded-lg hover:from-virgo-gold-400 hover:to-virgo-gold-500 transition-all font-medium"
          >
            <Sparkles className="h-5 w-5" />
            <span>Generate Document</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 mb-6">
        {[
          { id: 'search', label: 'Search Statutes', icon: Search },
          { id: 'research', label: 'AI Research', icon: Brain },
          { id: 'constitutions', label: 'Constitutions', icon: BookOpen },
          { id: 'generate', label: 'AI Document Generator', icon: FileEdit },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id);
              if (tab.id === 'constitutions' && constitutions.length === 0) {
                fetchConstitutions();
              }
              if (tab.id === 'generate') {
                setShowGenerateModal(true);
              }
              if (tab.id === 'research' && cases.length === 0) {
                fetchCases();
              }
            }}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
              activeTab === tab.id
                ? 'bg-virgo-teal-600 text-white'
                : 'bg-gradient-to-b from-slate-400 to-slate-500 text-slate-900 hover:from-slate-300 hover:to-slate-400 shadow-md'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Search Tab */}
      {activeTab === 'search' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Search Panel */}
          <div className="lg:col-span-1">
            <div className="bg-virgo-navy-800 border border-virgo-teal-900 rounded-xl p-6">
              <form onSubmit={handleSearch}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-virgo-teal-300 mb-2">Search Statutes</label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-virgo-teal-500" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="e.g., negligence, breach of contract..."
                        className="w-full pl-10 pr-4 py-3 bg-virgo-navy-700 border border-virgo-teal-800 rounded-lg text-white placeholder-virgo-teal-500 focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-virgo-teal-300 mb-2">
                      <Filter className="inline h-4 w-4 mr-1" />
                      Filter by State
                    </label>
                    <select
                      value={stateFilter}
                      onChange={(e) => setStateFilter(e.target.value)}
                      className="w-full px-4 py-2 bg-virgo-navy-700 border border-virgo-teal-800 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                    >
                      <option value="">All States</option>
                      {states.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 bg-gradient-to-r from-virgo-teal-600 to-virgo-teal-500 text-white rounded-lg hover:from-virgo-teal-500 hover:to-virgo-teal-400 transition-all font-medium disabled:opacity-50"
                  >
                    {loading ? (
                      <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                    ) : (
                      'Search'
                    )}
                  </button>
                </div>
              </form>

              {/* Search Results */}
              <div className="mt-6 space-y-2 max-h-[500px] overflow-y-auto">
                {searchResults.map((result, index) => (
                  <button
                    key={index}
                    onClick={() => fetchStatute(result.url || result.id)}
                    className="w-full text-left p-3 bg-virgo-navy-700 hover:bg-virgo-navy-600 rounded-lg transition-all border border-virgo-teal-900"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">
                          {result.title}
                        </p>
                        {result.summary && (
                          <p className="text-xs text-virgo-teal-400 mt-1 line-clamp-2">
                            {result.summary}
                          </p>
                        )}
                      </div>
                      <ChevronRight className="h-4 w-4 text-virgo-teal-500 flex-shrink-0 ml-2" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Statute Detail Panel */}
          <div className="lg:col-span-2">
            {selectedStatute ? (
              <div className="bg-virgo-navy-800 border border-virgo-teal-900 rounded-xl p-6">
                <div className="flex items-start justify-between mb-4">
                  <h2 className="text-xl font-semibold text-white">
                    {selectedStatute.title}
                  </h2>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => addStatuteToDocument(selectedStatute.url)}
                      className="flex items-center space-x-1 px-3 py-1.5 bg-virgo-gold-600 text-virgo-navy-900 rounded-lg hover:bg-virgo-gold-500 transition-all text-sm"
                    >
                      <Sparkles className="h-4 w-4" />
                      <span>Use in Document</span>
                    </button>
                    <button
                      onClick={() => downloadPDF(selectedStatute.url)}
                      className="flex items-center space-x-1 px-3 py-1.5 bg-virgo-teal-600 text-white rounded-lg hover:bg-virgo-teal-500 transition-all text-sm"
                    >
                      <Download className="h-4 w-4" />
                      <span>PDF</span>
                    </button>
                    {selectedStatute.url && (
                      <a
                        href={selectedStatute.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-1 px-3 py-1.5 bg-virgo-navy-600 text-virgo-teal-300 rounded-lg hover:bg-virgo-navy-500 transition-all text-sm"
                      >
                        <ExternalLink className="h-4 w-4" />
                        <span>Source</span>
                      </a>
                    )}
                  </div>
                </div>

                {selectedStatute.plain_language && (
                  <div className="mb-6 p-4 bg-virgo-gold-900/20 border border-virgo-gold-700/30 rounded-lg">
                    <h3 className="text-sm font-medium text-virgo-gold-400 mb-2 flex items-center">
                      <Sparkles className="h-4 w-4 mr-2" />
                      Plain Language Summary
                    </h3>
                    <p className="text-virgo-teal-200 text-sm">
                      {selectedStatute.plain_language}
                    </p>
                  </div>
                )}

                {selectedStatute.summary && (
                  <div className="mb-6 p-4 bg-virgo-teal-900/20 border border-virgo-teal-700/30 rounded-lg">
                    <h3 className="text-sm font-medium text-virgo-teal-400 mb-2">Summary</h3>
                    <p className="text-virgo-teal-200 text-sm">
                      {selectedStatute.summary}
                    </p>
                  </div>
                )}

                <div className="prose prose-invert max-w-none">
                  <h3 className="text-sm font-medium text-virgo-teal-400 mb-2">Full Text</h3>
                  <div className="bg-virgo-navy-700 p-4 rounded-lg max-h-[500px] overflow-y-auto">
                    <pre className="whitespace-pre-wrap text-sm text-virgo-teal-100 font-sans">
                      {selectedStatute.raw_text}
                    </pre>
                  </div>
                </div>

                {selectedStatute.cross_references && (
                  <div className="mt-4">
                    <h3 className="text-sm font-medium text-virgo-teal-400 mb-2">Cross References</h3>
                    <p className="text-sm text-virgo-teal-300">{selectedStatute.cross_references}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-virgo-navy-800 border border-virgo-teal-900 rounded-xl p-12 text-center">
                <BookOpen className="h-16 w-16 text-virgo-teal-700 mx-auto mb-4" />
                <p className="text-virgo-teal-400">
                  Search for a statute and select it to view details
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* AI Research Tab */}
      {activeTab === 'research' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Research Input Panel */}
          <div className="bg-virgo-navy-800 border border-virgo-teal-900 rounded-xl p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-lg">
                <Brain className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Astraea AI Legal Research</h2>
                <p className="text-sm text-virgo-teal-400">Ask questions in plain language</p>
              </div>
            </div>

            {!researchSession ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-virgo-teal-300 mb-2">Jurisdiction</label>
                  <select
                    value={selectedResearchState}
                    onChange={(e) => setSelectedResearchState(e.target.value)}
                    className="w-full px-4 py-2 bg-virgo-navy-700 border border-virgo-teal-800 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                  >
                    <option value="">Federal / General</option>
                    {states.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-virgo-teal-300 mb-2">Research Question</label>
                  <textarea
                    value={researchQuestion}
                    onChange={(e) => setResearchQuestion(e.target.value)}
                    rows={4}
                    placeholder="e.g., What are the requirements for proving negligence in a slip and fall case? What damages can be recovered?"
                    className="w-full px-4 py-3 bg-virgo-navy-700 border border-virgo-teal-800 rounded-lg text-white placeholder-virgo-teal-500 focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                  />
                </div>

                <button
                  onClick={startResearch}
                  disabled={researchLoading || !researchQuestion.trim()}
                  className="w-full py-3 bg-gradient-to-r from-cyan-600 to-cyan-500 text-white rounded-lg hover:from-cyan-500 hover:to-cyan-400 transition-all font-medium disabled:opacity-50 flex items-center justify-center space-x-2"
                >
                  {researchLoading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Analyzing...</span>
                    </>
                  ) : (
                    <>
                      <Brain className="h-5 w-5" />
                      <span>Start Research</span>
                    </>
                  )}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Conversation History */}
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {researchSession.conversation?.map((msg, index) => (
                    <div
                      key={index}
                      className={`p-3 rounded-lg ${
                        msg.role === 'user'
                          ? 'bg-virgo-teal-900/30 border border-virgo-teal-800 ml-8'
                          : 'bg-slate-900/30 border border-cyan-800 mr-8'
                      }`}
                    >
                      <div className="flex items-center space-x-2 mb-1">
                        {msg.role === 'user' ? (
                          <MessageSquare className="h-4 w-4 text-virgo-teal-400" />
                        ) : (
                          <Brain className="h-4 w-4 text-cyan-400" />
                        )}
                        <span className={`text-xs font-medium ${
                          msg.role === 'user' ? 'text-virgo-teal-400' : 'text-cyan-400'
                        }`}>
                          {msg.role === 'user' ? 'You' : 'Astraea AI'}
                        </span>
                      </div>
                      <p className="text-sm text-white whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  ))}
                </div>

                {/* Follow-up Questions */}
                {researchSession.status === 'needs_clarification' && researchSession.follow_up_questions?.length > 0 && (
                  <div className="p-4 bg-yellow-900/20 border border-yellow-700/30 rounded-lg">
                    <h3 className="text-sm font-medium text-yellow-400 mb-3 flex items-center">
                      <HelpCircle className="h-4 w-4 mr-2" />
                      Clarifying Questions
                    </h3>
                    <ul className="space-y-2 mb-4">
                      {researchSession.follow_up_questions.map((q, index) => (
                        <li key={index} className="text-sm text-yellow-200 flex items-start">
                          <span className="text-yellow-500 mr-2">{index + 1}.</span>
                          {q}
                        </li>
                      ))}
                    </ul>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={followUpAnswer}
                        onChange={(e) => setFollowUpAnswer(e.target.value)}
                        placeholder="Type your answer..."
                        className="flex-1 px-4 py-2 bg-virgo-navy-700 border border-virgo-teal-800 rounded-lg text-white placeholder-virgo-teal-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        onKeyPress={(e) => e.key === 'Enter' && submitFollowUp()}
                      />
                      <button
                        onClick={submitFollowUp}
                        disabled={researchLoading || !followUpAnswer.trim()}
                        className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-500 transition-colors disabled:opacity-50"
                      >
                        <Send className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Ready for Memo */}
                {researchSession.status === 'ready_for_memo' && (
                  <div className="p-4 bg-green-900/20 border border-green-700/30 rounded-lg">
                    <h3 className="text-sm font-medium text-green-400 mb-3 flex items-center">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Research Complete
                    </h3>
                    <p className="text-sm text-green-200 mb-4">
                      Astraea has gathered enough information. You can now generate a comprehensive legal research memo.
                    </p>
                    <button
                      onClick={generateMemo}
                      disabled={researchLoading}
                      className="w-full py-3 bg-gradient-to-r from-green-600 to-green-500 text-white rounded-lg hover:from-green-500 hover:to-green-400 transition-all font-medium disabled:opacity-50 flex items-center justify-center space-x-2"
                    >
                      {researchLoading ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin" />
                          <span>Generating Memo...</span>
                        </>
                      ) : (
                        <>
                          <FileText className="h-5 w-5" />
                          <span>Generate Legal Memo</span>
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* New Research Button */}
                <button
                  onClick={resetResearch}
                  className="w-full py-2 bg-virgo-navy-700 text-virgo-teal-300 rounded-lg hover:bg-virgo-navy-600 transition-colors text-sm"
                >
                  Start New Research
                </button>
              </div>
            )}
          </div>

          {/* Memo Display Panel */}
          <div className="bg-virgo-navy-800 border border-virgo-teal-900 rounded-xl p-6">
            {researchSession?.status === 'memo_generated' && researchSession.memo ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-white flex items-center">
                    <FileText className="h-5 w-5 mr-2 text-virgo-gold-400" />
                    Legal Research Memo
                  </h2>
                  <button
                    onClick={downloadMemo}
                    className="flex items-center space-x-1 px-3 py-1.5 bg-virgo-teal-600 text-white rounded-lg hover:bg-virgo-teal-500 transition-all text-sm"
                  >
                    <Download className="h-4 w-4" />
                    <span>Download DOCX</span>
                  </button>
                </div>

                {/* Memo Content */}
                <div className="bg-virgo-navy-700 rounded-lg p-4 max-h-[400px] overflow-y-auto">
                  <div className="prose prose-invert prose-sm max-w-none">
                    <h3 className="text-virgo-gold-400">{researchSession.memo.title}</h3>

                    <div className="text-virgo-teal-200 text-sm mb-4">
                      <p><strong>Question Presented:</strong></p>
                      <p className="whitespace-pre-wrap">{researchSession.memo.question_presented}</p>
                    </div>

                    <div className="text-virgo-teal-200 text-sm mb-4">
                      <p><strong>Brief Answer:</strong></p>
                      <p className="whitespace-pre-wrap">{researchSession.memo.brief_answer}</p>
                    </div>

                    <div className="text-virgo-teal-200 text-sm mb-4">
                      <p><strong>Analysis:</strong></p>
                      <p className="whitespace-pre-wrap">{researchSession.memo.analysis}</p>
                    </div>

                    {researchSession.memo.citations?.length > 0 && (
                      <div className="text-virgo-teal-200 text-sm mb-4">
                        <p><strong>Citations:</strong></p>
                        <ul className="list-disc pl-4">
                          {researchSession.memo.citations.map((citation, index) => (
                            <li key={index}>{citation}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="text-virgo-teal-200 text-sm">
                      <p><strong>Conclusion:</strong></p>
                      <p className="whitespace-pre-wrap">{researchSession.memo.conclusion}</p>
                    </div>
                  </div>
                </div>

                {/* Save to Case */}
                <div className="p-4 bg-virgo-navy-700 rounded-lg">
                  <h3 className="text-sm font-medium text-virgo-teal-300 mb-3 flex items-center">
                    <FolderInput className="h-4 w-4 mr-2" />
                    Save to Case Documents
                  </h3>
                  {memoSaved ? (
                    <div className="flex items-center space-x-2 text-green-400">
                      <CheckCircle className="h-5 w-5" />
                      <span>Memo saved to case documents!</span>
                    </div>
                  ) : (
                    <div className="flex space-x-2">
                      <select
                        value={selectedCase}
                        onChange={(e) => setSelectedCase(e.target.value)}
                        className="flex-1 px-4 py-2 bg-virgo-navy-600 border border-virgo-teal-800 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                      >
                        <option value="">Select a case...</option>
                        {cases.map(c => (
                          <option key={c.id} value={c.id}>
                            {c.client_name} - {c.case_name}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={saveMemoToCase}
                        disabled={savingMemo || !selectedCase}
                        className="px-4 py-2 bg-virgo-gold-600 text-virgo-navy-900 rounded-lg hover:bg-virgo-gold-500 transition-colors disabled:opacity-50 flex items-center space-x-1"
                      >
                        {savingMemo ? (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                          <>
                            <FolderInput className="h-4 w-4" />
                            <span>Save</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-center py-12">
                <div>
                  <Brain className="h-16 w-16 text-virgo-teal-700 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-virgo-teal-400 mb-2">AI-Powered Legal Research</h3>
                  <p className="text-virgo-teal-500 text-sm max-w-sm">
                    Ask a legal question and Astraea will research relevant statutes, case law, and generate a comprehensive memo with citations.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Constitutions Tab */}
      {activeTab === 'constitutions' && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-1">
            <div className="bg-virgo-navy-800 border border-virgo-teal-900 rounded-xl p-4">
              <h3 className="text-lg font-medium text-white mb-4 flex items-center">
                <Globe className="h-5 w-5 mr-2 text-virgo-gold-400" />
                State Constitutions
              </h3>
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {constitutions.map((constitution, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedConstitution(constitution)}
                    className={`w-full text-left p-3 rounded-lg transition-all ${
                      selectedConstitution && selectedConstitution.state_code === constitution.state_code
                        ? 'bg-virgo-teal-600 text-white'
                        : 'bg-virgo-navy-700 text-virgo-teal-300 hover:bg-virgo-navy-600'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{constitution.title}</span>
                      <span className="text-xs opacity-75">{constitution.section_count} sections</span>
                    </div>
                  </button>
                ))}
                {constitutions.length === 0 && (
                  <p className="text-virgo-teal-500 text-center py-4">Loading constitutions...</p>
                )}
              </div>
            </div>
          </div>
          <div className="lg:col-span-3">
            {selectedConstitution ? (
              <div className="bg-virgo-navy-800 border border-virgo-teal-900 rounded-xl p-6">
                <h2 className="text-xl font-semibold text-white mb-4">
                  {selectedConstitution.title}
                </h2>
                <p className="text-virgo-teal-400">
                  {selectedConstitution.section_count} sections available
                </p>
              </div>
            ) : (
              <div className="bg-virgo-navy-800 border border-virgo-teal-900 rounded-xl p-12 text-center">
                <BookOpen className="h-16 w-16 text-virgo-teal-700 mx-auto mb-4" />
                <p className="text-virgo-teal-400">Select a constitution to view</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Generate Document Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-virgo-navy-800 border border-virgo-teal-900 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-virgo-teal-900 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-white flex items-center">
                <Sparkles className="h-6 w-6 mr-2 text-virgo-gold-400" />
                AI Document Generator
              </h2>
              <button
                onClick={() => setShowGenerateModal(false)}
                className="p-2 hover:bg-virgo-navy-700 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-virgo-teal-400" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-virgo-teal-300 mb-2">Document Type</label>
                  <select
                    value={generateForm.document_type}
                    onChange={(e) => setGenerateForm({...generateForm, document_type: e.target.value})}
                    className="w-full px-4 py-2 bg-virgo-navy-700 border border-virgo-teal-800 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                  >
                    {documentTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-virgo-teal-300 mb-2">Document Title *</label>
                  <input
                    type="text"
                    value={generateForm.title}
                    onChange={(e) => setGenerateForm({...generateForm, title: e.target.value})}
                    placeholder="e.g., Motion to Dismiss"
                    className="w-full px-4 py-2 bg-virgo-navy-700 border border-virgo-teal-800 rounded-lg text-white placeholder-virgo-teal-500 focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-virgo-teal-300 mb-2">Client Name</label>
                  <input
                    type="text"
                    value={generateForm.client_name}
                    onChange={(e) => setGenerateForm({...generateForm, client_name: e.target.value})}
                    className="w-full px-4 py-2 bg-virgo-navy-700 border border-virgo-teal-800 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-virgo-teal-300 mb-2">Opposing Party</label>
                  <input
                    type="text"
                    value={generateForm.opposing_party}
                    onChange={(e) => setGenerateForm({...generateForm, opposing_party: e.target.value})}
                    className="w-full px-4 py-2 bg-virgo-navy-700 border border-virgo-teal-800 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-virgo-teal-300 mb-2">Court Name</label>
                  <input
                    type="text"
                    value={generateForm.court_name}
                    onChange={(e) => setGenerateForm({...generateForm, court_name: e.target.value})}
                    placeholder="e.g., Circuit Court of the 15th Judicial Circuit"
                    className="w-full px-4 py-2 bg-virgo-navy-700 border border-virgo-teal-800 rounded-lg text-white placeholder-virgo-teal-500 focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-virgo-teal-300 mb-2">Case Number</label>
                  <input
                    type="text"
                    value={generateForm.case_number}
                    onChange={(e) => setGenerateForm({...generateForm, case_number: e.target.value})}
                    className="w-full px-4 py-2 bg-virgo-navy-700 border border-virgo-teal-800 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-virgo-teal-300 mb-2">Case Context / Facts</label>
                <textarea
                  value={generateForm.case_context}
                  onChange={(e) => setGenerateForm({...generateForm, case_context: e.target.value})}
                  rows={3}
                  placeholder="Describe the relevant facts and circumstances..."
                  className="w-full px-4 py-2 bg-virgo-navy-700 border border-virgo-teal-800 rounded-lg text-white placeholder-virgo-teal-500 focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                />
              </div>

              <div>
                <label className="block text-sm text-virgo-teal-300 mb-2">Additional Instructions</label>
                <textarea
                  value={generateForm.additional_instructions}
                  onChange={(e) => setGenerateForm({...generateForm, additional_instructions: e.target.value})}
                  rows={2}
                  placeholder="Any specific requirements or points to include..."
                  className="w-full px-4 py-2 bg-virgo-navy-700 border border-virgo-teal-800 rounded-lg text-white placeholder-virgo-teal-500 focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                />
              </div>

              {generateForm.statute_urls.length > 0 && (
                <div>
                  <label className="block text-sm text-virgo-teal-300 mb-2">Referenced Statutes</label>
                  <div className="flex flex-wrap gap-2">
                    {generateForm.statute_urls.map((url, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 bg-virgo-teal-900/50 text-virgo-teal-300 rounded-full text-sm"
                      >
                        <FileText className="h-3 w-3 mr-1" />
                        Statute {index + 1}
                        <button
                          onClick={() => setGenerateForm({
                            ...generateForm,
                            statute_urls: generateForm.statute_urls.filter((_, i) => i !== index)
                          })}
                          className="ml-2 hover:text-red-400"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-virgo-teal-900 flex justify-end space-x-3">
              <button
                onClick={() => setShowGenerateModal(false)}
                className="px-4 py-2 bg-virgo-navy-700 text-virgo-teal-300 rounded-lg hover:bg-virgo-navy-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={generateDocument}
                disabled={!generateForm.title || generating}
                className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-virgo-gold-500 to-virgo-gold-600 text-virgo-navy-900 rounded-lg hover:from-virgo-gold-400 hover:to-virgo-gold-500 transition-all font-medium disabled:opacity-50"
              >
                {generating ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="h-5 w-5" />
                    <span>Generate DOCX</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default LawLibrary;
